"use client"

import * as React from "react"
import { Search, Settings, User, ShoppingBag, ArrowRight, Layers, X, Sparkles, Plus } from "lucide-react"
import { AppIcon } from "@/components/desk/AppIcon"
import { App, InstalledApp } from "@/types/workspace"

interface NewTabViewProps {
  tabInstanceId: string
  installedApps: InstalledApp[]
  hiddenAppIds?: string[]
  onSelectApp: (app: App) => void
  onSelectSettings: () => void
  onSelectProfile: () => void
  onSelectMarketplace: () => void
  canAccessMarketplace?: boolean
}

export function NewTabView({
  tabInstanceId,
  installedApps = [],
  hiddenAppIds = [],
  onSelectApp,
  onSelectSettings,
  onSelectProfile,
  onSelectMarketplace,
  canAccessMarketplace = true
}: NewTabViewProps) {
  const [searchTerm, setSearchTerm] = React.useState("")
  const [activeCategory, setActiveCategory] = React.useState<"all" | "system" | "apps">("all")

  // Filtra aplicativos instalados e ativos
  const activeInstalledApps = React.useMemo(() => {
    return installedApps.filter((ia) => {
      if (ia.status !== "active") return false
      if (hiddenAppIds.includes(ia.app.id)) return false
      return true
    })
  }, [installedApps, hiddenAppIds])

  // Filtragem por termo de busca
  const filteredApps = React.useMemo(() => {
    if (!searchTerm.trim()) return activeInstalledApps
    const term = searchTerm.toLowerCase().trim()
    return activeInstalledApps.filter((ia) => {
      const name = (ia.app.name || "").toLowerCase()
      const desc = (ia.app.description || "").toLowerCase()
      const slug = (ia.app.slug || "").toLowerCase()
      return name.includes(term) || desc.includes(term) || slug.includes(term)
    })
  }, [activeInstalledApps, searchTerm])

  const systemServices = React.useMemo(() => {
    const items = [
      {
        id: "settings",
        title: "Ajustes do Sistema",
        description: "Configurações da empresa, membros, ambientes e personalizações.",
        badge: "Sistema",
        icon: Settings,
        iconBg: "bg-blue-500/10 text-[#004EB1] dark:bg-blue-500/20 dark:text-blue-400",
        hoverBorder: "hover:border-blue-500/50 dark:hover:border-blue-500/40",
        action: onSelectSettings
      },
      {
        id: "profile",
        title: "Meu Perfil",
        description: "Gerencie dados pessoais, credenciais, segurança 2FA e preferências.",
        badge: "Perfil",
        icon: User,
        iconBg: "bg-purple-500/10 text-purple-600 dark:bg-purple-500/20 dark:text-purple-400",
        hoverBorder: "hover:border-purple-500/50 dark:hover:border-purple-500/40",
        action: onSelectProfile
      }
    ]
    if (canAccessMarketplace) {
      items.push({
        id: "marketplace",
        title: "Loja de Aplicativos",
        description: "Explore e instale novas integrações, extensões e apps oficiais.",
        badge: "Marketplace",
        icon: ShoppingBag,
        iconBg: "bg-emerald-500/10 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400",
        hoverBorder: "hover:border-emerald-500/50 dark:hover:border-emerald-500/40",
        action: onSelectMarketplace
      })
    }
    return items
  }, [canAccessMarketplace, onSelectMarketplace, onSelectProfile, onSelectSettings])

  const filteredSystemServices = React.useMemo(() => {
    if (!searchTerm.trim()) return systemServices
    const term = searchTerm.toLowerCase().trim()
    return systemServices.filter((s) => {
      return (
        s.title.toLowerCase().includes(term) ||
        s.description.toLowerCase().includes(term) ||
        s.badge.toLowerCase().includes(term)
      )
    })
  }, [searchTerm, systemServices])

  return (
    <div className="flex-1 w-full h-full overflow-y-auto bg-gradient-to-b from-zinc-50 via-white to-zinc-50 dark:from-zinc-950 dark:via-zinc-900/30 dark:to-zinc-950 p-6 md:p-10 font-sans select-none">
      <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in duration-200">

        {/* Cabeçalho da Nova Guia */}
        <div className="flex flex-col items-center text-center space-y-3 pt-2 sm:pt-6">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-900 dark:text-white tracking-tight">
            Nova Guia
          </h1>

          <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-400 max-w-lg">
            Selecione qual dos aplicativos deseja abrir.
          </p>

          {/* Barra de Busca e Filtros */}
          <div className="w-full max-w-md pt-2">
            <div className="relative flex items-center">
              <Search className="absolute left-3.5 size-4 text-zinc-400 pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar aplicativo, serviço ou ajuste..."
                className="w-full pl-10 pr-9 py-2.5 text-xs sm:text-sm rounded-xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 focus:outline-hidden focus:ring-2 focus:ring-[#004EB1]/30 dark:focus:ring-blue-500/30 focus:border-[#004EB1] dark:focus:border-blue-500 text-zinc-800 dark:text-zinc-200 shadow-xs transition-all"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 p-0.5 rounded-full text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors cursor-pointer"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 1. SEÇÃO DE SERVIÇOS DO SISTEMA */}
        {(activeCategory === "all" || activeCategory === "system") && filteredSystemServices.length > 0 && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
              {filteredSystemServices.map((service) => {
                const IconComp = service.icon
                return (
                  <button
                    key={service.id}
                    type="button"
                    onClick={service.action}
                    className={`group text-left p-4 rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800/80 shadow-xs hover:shadow-md ${service.hoverBorder} hover:-translate-y-0.5 transition-all duration-200 flex flex-col justify-between cursor-pointer`}
                  >
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <div className={`size-10 rounded-xl flex items-center justify-center ${service.iconBg} shadow-xs`}>
                          <IconComp className="size-5" />
                        </div>
                      </div>

                      <h3 className="text-sm font-bold text-zinc-900 dark:text-zinc-100 group-hover:text-[#004EB1] dark:group-hover:text-blue-400 transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 line-clamp-2 leading-relaxed">
                        {service.description}
                      </p>
                    </div>

                    <div className="pt-4 mt-3 border-t border-zinc-100 dark:border-zinc-800/80 flex items-center justify-between text-xs font-semibold text-[#004EB1] dark:text-blue-400">
                      <span>Abrir nesta guia</span>
                      <ArrowRight className="size-3.5 transform group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* 2. SEÇÃO DE APLICATIVOS DO WORKSPACE */}
        {(activeCategory === "all" || activeCategory === "apps") && (
          <div className="space-y-3">
            {filteredApps.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3.5">
                {filteredApps.map(({ app }) => (
                  <button
                    key={app.id}
                    type="button"
                    onClick={() => onSelectApp(app)}
                    className="group text-left p-3.5 rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800/80 shadow-xs hover:shadow-md hover:border-[#004EB1]/50 dark:hover:border-blue-500/50 hover:-translate-y-0.5 transition-all duration-200 flex flex-col justify-between cursor-pointer"
                  >
                    <div className="flex items-start gap-3">
                      <div className="shrink-0 group-hover:scale-105 transition-transform flex items-center justify-center">
                        <AppIcon
                          iconUrl={app.iconUrl}
                          iframeUrl={app.iframeUrl}
                          name={app.name}
                          size="medium"
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h4 className="text-xs font-bold text-zinc-900 dark:text-zinc-100 truncate group-hover:text-[#004EB1] dark:group-hover:text-blue-400 transition-colors">
                          {app.name}
                        </h4>
                        <p className="text-[11px] text-zinc-400 dark:text-zinc-500 line-clamp-2 mt-0.5 leading-snug">
                          {app.description || "Aplicativo integrado do Bold Workspace."}
                        </p>
                      </div>
                    </div>

                    <div className="pt-3 mt-3 border-t border-zinc-100 dark:border-zinc-800/60 flex items-center justify-between text-[11px] font-semibold text-zinc-500 dark:text-zinc-400 group-hover:text-[#004EB1] dark:group-hover:text-blue-400 transition-colors">
                      <span>Abrir na Guia</span>
                      <ArrowRight className="size-3 transform group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-8 rounded-2xl bg-white dark:bg-zinc-900 border border-dashed border-zinc-300 dark:border-zinc-800 text-center space-y-3">
                <div className="size-12 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center mx-auto text-zinc-400">
                  <Search className="size-5" />
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-bold text-zinc-700 dark:text-zinc-300">
                    {searchTerm
                      ? `Nenhum aplicativo encontrado para "${searchTerm}"`
                      : "Nenhum aplicativo instalado disponível neste ambiente"}
                  </p>
                  {canAccessMarketplace && (
                    <p className="text-[11px] text-zinc-400">
                      Você pode acessar a Loja de Aplicativos para instalar novas ferramentas neste ambiente.
                    </p>
                  )}
                </div>
                {canAccessMarketplace && (
                  <div>
                    <button
                      type="button"
                      onClick={onSelectMarketplace}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-[#004EB1] hover:bg-[#003B85] text-white transition-colors cursor-pointer"
                    >
                      <ShoppingBag className="size-3.5" />
                      <span>Ir para a Loja de Aplicativos</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  )
}
